import { useEffect, useState } from "react";
import { Outlet, NavLink, useParams, useNavigate, Link, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABEL } from "../lib/format";
import { toast } from "sonner";
import {
  LayoutDashboard, Map, FileStack, Lock, ChevronDown, ChevronRight, Bell, ScrollText,
  Users, BookOpenCheck, LogOut, ArrowLeftRight, Waves, Building2, Hammer, Eye,
  Calculator, Landmark, Store, Briefcase, Unlock, Menu, X, Building, Search,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";

const LOCKED_MODULES = [
  { nombre: "Supervisión", icon: Eye },
  { nombre: "Administración Contable", icon: Calculator },
];

function GlobalSearch({ pid }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => api.get("/buscar", { params: { q } }).then((r) => setRes(r.data.resultados)), 350);
    return () => clearTimeout(t);
  }, [q]);
  const go = (r) => {
    const p = r.property_id || pid;
    const rutas = { tramite: `/p/${p}/tramites/${r.id}`, tarea: `/p/${p}/gestion`, minuta: `/p/${p}/gestion`,
      contacto: `/p/${p}/gestion`, producto: `/p/${p}/desarrollo`, credito: `/p/${p}/creditos/${r.id}`, documento: `/p/${p}/tramites` };
    navigate(rutas[r.tipo] || `/p/${p}/dashboard`);
    setQ(""); setRes(null);
  };
  return (
    <div className="relative mb-2 px-1">
      <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-md px-3">
        <Search size={13} className="text-white/40 flex-shrink-0" />
        <input data-testid="buscador-global" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en todo..."
          className="bg-transparent text-sm text-white placeholder:text-white/30 py-2 w-full outline-none" />
      </div>
      {res && (
        <div className="absolute left-1 right-1 top-full mt-1 bg-[#002B49] border border-white/15 rounded-md shadow-2xl z-50 max-h-72 overflow-y-auto" data-testid="buscador-resultados">
          {res.length === 0 && <p className="text-xs text-white/40 p-3">Sin resultados</p>}
          {res.map((r, i) => (
            <button key={i} onClick={() => go(r)} className="w-full text-left px-3 py-2 hover:bg-white/10 border-b border-white/5">
              <p className="text-xs text-white font-semibold truncate">{r.titulo || "(sin título)"}</p>
              <p className="text-[10px] text-white/40 truncate uppercase">{r.tipo} · {r.sub}</p>
            </button>))}
        </div>)}
    </div>
  );
}

function NavItem({ to, icon: Icon, children, badge, testId }) {
  return (
    <NavLink to={to} data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 text-sm rounded-md transition-colors duration-200 ${
          isActive ? "bg-white/10 text-white border-l-2 border-[#00A3B5]" : "text-white/60 hover:text-white hover:bg-white/5"
        }`}>
      <Icon size={16} />
      <span className="flex-1 truncate">{children}</span>
      {badge > 0 && <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{badge}</span>}
    </NavLink>
  );
}

export default function WorkspaceLayout() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const [prop, setProp] = useState(null);
  const [alertCount, setAlertCount] = useState(0);
  const [fasesOpen, setFasesOpen] = useState(true);
  const [openFase, setOpenFase] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
    api.get(`/properties/${pid}`).then((r) => {
      setProp(r.data);
      const activa = r.data.fases?.find((f) => f.activa);
      setOpenFase(activa?.id || null);
    });
    api.get(`/properties/${pid}/alertas`).then((r) => setAlertCount(r.data.length));
  }, [pid, location.pathname]);

  const activarFase = async (f) => {
    try {
      await api.post(`/phases/${f.id}/activate`);
      toast.success(`${f.nombre} activada`);
      const r = await api.get(`/properties/${pid}`);
      setProp(r.data);
    } catch (e) {
      toast.error("No se pudo activar la fase");
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-col lg:flex-row h-screen overflow-hidden bg-slate-50">
        {/* Barra superior móvil */}
        <div className="lg:hidden flex items-center gap-3 bg-[#001D33] text-white px-4 py-3 flex-shrink-0 z-30">
          <button data-testid="mobile-menu-button" onClick={() => setSidebarOpen(true)} className="p-1 -ml-1">
            <Menu size={20} />
          </button>
          <p className="font-display font-bold text-sm truncate flex-1">{prop?.nombre || "..."}</p>
          <Link to="/" data-testid="mobile-home-link" className="text-white/60 p-1" title="Cambiar proyecto">
            <ArrowLeftRight size={16} />
          </Link>
        </div>
        {sidebarOpen && (
          <div data-testid="sidebar-backdrop" className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)} />
        )}
        <aside className={`w-72 flex-shrink-0 bg-[#001D33] text-white flex flex-col fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 lg:static lg:translate-x-0 lg:z-auto ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
          <div className="p-5 border-b border-white/10 flex items-center gap-2">
            <Link to="/" data-testid="back-to-home-link" className="flex items-center gap-3 group flex-1 min-w-0">
              <div className="w-9 h-9 rounded-md bg-[#00A3B5] flex items-center justify-center flex-shrink-0">
                <Building size={18} />
              </div>
              <div className="min-w-0">
                <p className="font-display font-bold text-sm leading-tight truncate">{prop?.nombre || "..."}</p>
                <p className="text-white/40 text-[10px] flex items-center gap-1 mt-0.5">
                  <ArrowLeftRight size={9} /> Cambiar proyecto
                </p>
              </div>
            </Link>
            <button data-testid="close-sidebar-button" onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white/50 hover:text-white p-1">
              <X size={18} />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-1">
            <GlobalSearch pid={pid} />
            <NavItem to={`/p/${pid}/dashboard`} icon={LayoutDashboard} testId="nav-dashboard">Vista General del Macroproyecto</NavItem>
            <NavItem to={`/p/${pid}/mapa`} icon={Map} testId="nav-mapa">Mapa Maestro</NavItem>

            <button data-testid="nav-fases-toggle" onClick={() => setFasesOpen(!fasesOpen)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-white/60 hover:text-white transition-colors">
              {fasesOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <span className="flex-1 text-left font-semibold uppercase tracking-wider text-xs">Fases</span>
            </button>
            {fasesOpen && prop?.fases?.map((f) => (
              <div key={f.id} className="ml-2">
                <div className={`flex items-center gap-2 px-3 py-2 text-sm rounded-md ${f.activa ? "text-white" : "text-white/30"}`}>
                  {f.activa ? (
                    <button data-testid={`fase-${f.id}-toggle`} onClick={() => setOpenFase(openFase === f.id ? null : f.id)}
                      className="flex items-center gap-2 flex-1 text-left">
                      {openFase === f.id ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      <span className="truncate text-xs font-semibold">{f.nombre}</span>
                    </button>
                  ) : (
                    <>
                      <Lock size={12} />
                      <span className="truncate text-xs flex-1">{f.nombre}</span>
                      {isAdmin && (
                        <button data-testid={`activar-fase-${f.id}`} onClick={() => activarFase(f)}
                          className="text-[10px] text-[#00A3B5] hover:text-white flex items-center gap-1 font-bold uppercase">
                          <Unlock size={10} /> Activar
                        </button>
                      )}
                    </>
                  )}
                </div>
                {f.activa && openFase === f.id && (
                  <div className="ml-5 space-y-0.5 border-l border-white/10 pl-2">
                    <NavItem to={`/p/${pid}/tramites`} icon={FileStack} testId="nav-tramites">Tramitología y Permisología</NavItem>
                    <NavItem to={`/p/${pid}/gestion`} icon={Briefcase} testId="nav-gestion">Gestión</NavItem>
                    <NavItem to={`/p/${pid}/desarrollo`} icon={Building2} testId="nav-desarrollo">Desarrollo</NavItem>
                    <NavItem to={`/p/${pid}/creditos`} icon={Landmark} testId="nav-creditos">Préstamos / Financiamiento</NavItem>
                    <NavItem to={`/p/${pid}/construccion`} icon={Hammer} testId="nav-construccion">Construcción</NavItem>
                    <NavItem to={`/p/${pid}/comercializacion`} icon={Store} testId="nav-comercializacion">Comercialización</NavItem>
                    {LOCKED_MODULES.map((m) => (
                      <Tooltip key={m.nombre}>
                        <TooltipTrigger asChild>
                          <div data-testid={`modulo-bloqueado-${m.nombre.split(" ")[0].toLowerCase()}`}
                            className="flex items-center gap-3 px-4 py-2 text-sm text-white/25 cursor-not-allowed select-none">
                            <m.icon size={15} />
                            <span className="flex-1 truncate text-xs">{m.nombre}</span>
                            <Lock size={11} />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="bg-[#002B49] text-white border-white/10">
                          Se activará en fases posteriores del sistema
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div className="pt-3 mt-3 border-t border-white/10 space-y-1">
              <NavItem to={`/p/${pid}/catalogo`} icon={BookOpenCheck} testId="nav-catalogo">Catálogo de Permisología</NavItem>
              <NavItem to={`/p/${pid}/alertas`} icon={Bell} badge={alertCount} testId="nav-alertas">Bandeja de Alertas</NavItem>
              <NavItem to={`/p/${pid}/auditoria`} icon={ScrollText} testId="nav-auditoria">Auditoría / Bitácora</NavItem>
              {isAdmin && <NavItem to={`/p/${pid}/usuarios`} icon={Users} testId="nav-usuarios">Usuarios</NavItem>}
            </div>
          </nav>
          <div className="p-4 border-t border-white/10 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#EAD8C0] text-[#002B49] font-display font-bold flex items-center justify-center text-sm flex-shrink-0">
              {user?.nombre?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{user?.nombre}</p>
              <p className="text-white/40 text-xs">{ROLE_LABEL[user?.role]}</p>
            </div>
            <button data-testid="sidebar-logout-button" onClick={async () => { await logout(); navigate("/login"); }}
              className="text-white/40 hover:text-white transition-colors" title="Cerrar sesión">
              <LogOut size={16} />
            </button>
          </div>
        </aside>
        <main className="flex-1 overflow-y-auto scrollbar-thin min-h-0">
          <Outlet context={{ prop, alertCount }} />
        </main>
      </div>
    </TooltipProvider>
  );
}
